import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, LogOut, ArrowLeft, Home, HelpCircle, Monitor, Tv, Smartphone, Subtitles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { tvKey } from './tvKeys';
import { popNav, popOk, isFireTvWrapper } from './tvAssets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KIND_LABEL = { tv: 'Téléviseur', mobile: 'Mobile / tablette', web: 'Navigateur web' };

export const SUB_SIZES = [{ id: 'small', label: 'Petit' }, { id: 'medium', label: 'Moyen' }, { id: 'large', label: 'Grand' }];
export const SUB_BGS = [{ id: 'none', label: 'Aucun' }, { id: 'shadow', label: 'Ombré' }, { id: 'box', label: 'Fond noir' }];
export const loadSubStyle = () => {
  try { const s = JSON.parse(localStorage.getItem('tv_sub_style') || 'null'); if (s && s.size && s.bg) return s; } catch { /* ignore */ }
  return { size: 'medium', bg: 'box' };
};
export const SUB_FONT = { small: '1.9vw', medium: '2.6vw', large: '3.5vw' };
export const SUB_BG_CSS = {
  none: { textShadow: '0 0 0.35vw rgba(0,0,0,0.9)' },
  shadow: { textShadow: '0.12vw 0.12vw 0.25vw #000, -0.12vw 0.12vw 0.25vw #000, 0 0 0.7vw #000' },
  box: { background: 'rgba(0,0,0,0.72)', padding: '0.35vw 1vw', borderRadius: '0.35vw' },
};

export function TvSettings({ onClose }) {
  const { user, activeProfile, setActiveProfile, logout } = useAuth();
  const [idx, setIdx] = useState(0);
  const [showHelp, setShowHelp] = useState(false);
  const [showDevices, setShowDevices] = useState(false);
  const [devices, setDevices] = useState(null);
  const [devIdx, setDevIdx] = useState(0);
  const [showSubs, setShowSubs] = useState(false);
  const [subRow, setSubRow] = useState(0); // 0 = taille, 1 = fond
  const [subStyle, setSubStyle] = useState(loadSubStyle);

  const saveSubStyle = (next) => {
    setSubStyle(next);
    try { localStorage.setItem('tv_sub_style', JSON.stringify(next)); } catch { /* ignore */ }
  };
  const cycleSub = (dir) => {
    const list = subRow === 0 ? SUB_SIZES : SUB_BGS;
    const key = subRow === 0 ? 'size' : 'bg';
    const cur = list.findIndex(o => o.id === subStyle[key]);
    const nxt = Math.min(list.length - 1, Math.max(0, cur + dir));
    if (nxt !== cur) saveSubStyle({ ...subStyle, [key]: list[nxt].id });
  };

  const openDevices = () => {
    setDevices(null);
    setDevIdx(0);
    setShowDevices(true);
    axios.get(`${API}/tv/devices`, { withCredentials: true })
      .then(r => setDevices(r.data || []))
      .catch(() => setDevices([]));
  };

  const disconnectDevice = (d) => {
    if (!d) return;
    axios.delete(`${API}/tv/devices/${d.id}`, { withCredentials: true })
      .then(() => {
        if (d.is_current) { logout(); onClose(); return; }
        setDevices(list => {
          const next = (list || []).filter(x => x.id !== d.id);
          setDevIdx(i => Math.min(i, Math.max(0, next.length - 1)));
          return next;
        });
      })
      .catch(() => {});
  };

  const options = [
    { id: 'profile', label: 'Changer de profil', desc: `Profil actuel : ${activeProfile?.name || '—'}`, Icon: Users },
    { id: 'subtitles', label: 'Style des sous-titres', desc: 'Taille et fond des sous-titres', Icon: Subtitles },
    { id: 'devices', label: 'Appareils connectés', desc: 'Déconnecter le compte sur d\'autres écrans', Icon: Monitor },
    { id: 'back', label: "Revenir à l'accueil", desc: '', Icon: Home },
    { id: 'help', label: "Obtenir de l'aide", desc: '', Icon: HelpCircle },
    { id: 'logout', label: 'Se déconnecter', desc: user?.email || '', Icon: LogOut },
  ];

  const select = (opt) => {
    if (opt.id === 'profile') { setActiveProfile(null); onClose(); }
    else if (opt.id === 'subtitles') { setSubRow(0); setShowSubs(true); }
    else if (opt.id === 'devices') openDevices();
    else if (opt.id === 'logout') { logout(); onClose(); }
    else if (opt.id === 'help') setShowHelp(true);
    else onClose();
  };

  useEffect(() => {
    const onKey = (e) => {
      const k = tvKey(e);
      if (!k) return;
      e.preventDefault();
      e.stopPropagation();
      if (showHelp) { if (k === 'back' || k === 'ok') { popOk(); setShowHelp(false); } return; }
      if (showSubs) {
        if (k === 'back' || k === 'ok') { popOk(); setShowSubs(false); }
        else if (k === 'up') { if (subRow > 0) popNav(); setSubRow(0); }
        else if (k === 'down') { if (subRow < 1) popNav(); setSubRow(1); }
        else if (k === 'left') { popNav(); cycleSub(-1); }
        else if (k === 'right') { popNav(); cycleSub(1); }
        return;
      }
      if (showDevices) {
        const list = devices || [];
        if (k === 'back') { popOk(); setShowDevices(false); }
        else if (k === 'up') { if (devIdx > 0) popNav(); setDevIdx(i => Math.max(0, i - 1)); }
        else if (k === 'down') { if (devIdx < list.length - 1) popNav(); setDevIdx(i => Math.min(Math.max(0, list.length - 1), i + 1)); }
        else if (k === 'ok' && list[devIdx]) { popOk(); disconnectDevice(list[devIdx]); }
        return;
      }
      if (k === 'back') { onClose(); return; }
      if (k === 'up') { if (idx > -1) popNav(); setIdx(i => Math.max(-1, i - 1)); }
      else if (k === 'down') { if (idx < options.length - 1) popNav(); setIdx(i => Math.min(options.length - 1, i + 1)); }
      else if (k === 'ok') { popOk(); if (idx === -1) onClose(); else select(options[idx]); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  });

  return (
    <div className="tv-fade-in" style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative' }} data-testid="tv-settings">
      <div className={`tv-btn ${idx === -1 ? 'tv-focused' : ''}`} onClick={onClose}
        style={{ position: 'absolute', top: '1.5vw', left: '2vw', display: 'flex', alignItems: 'center', gap: '0.5vw',
          padding: '0.45vw 1.2vw', borderRadius: '2vw', background: idx === -1 ? '#E9641A' : 'rgba(255,255,255,0.12)', cursor: 'pointer', fontSize: '1vw', fontWeight: 700 }}
        data-testid="tv-back-btn">
        <ArrowLeft style={{ width: '1vw', height: '1vw' }} />
        Retour
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '1.2vw', marginBottom: '2.5vw', background: 'rgba(255,255,255,0.05)', padding: '1vw 2vw', borderRadius: '0.8vw' }} data-testid="tv-settings-profile-card">
        <div style={{ width: '4.5vw', height: '4.5vw', borderRadius: '0.6vw', overflow: 'hidden', background: activeProfile?.avatar_color || '#E9641A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2vw', fontWeight: 800 }}>
          {activeProfile?.avatar_url
            ? <img src={activeProfile.avatar_url} alt={activeProfile?.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : (activeProfile?.name || '?')[0].toUpperCase()}
        </div>
        <div>
          <p style={{ fontSize: '1.3vw', fontWeight: 700 }}>{activeProfile?.name}</p>
          <p style={{ fontSize: '1vw', color: 'rgba(255,255,255,0.5)' }}>{user?.email}</p>
        </div>
      </div>

      <div style={{ width: '32vw', display: 'flex', flexDirection: 'column', gap: '0.8vw' }}>
        {options.map((opt, i) => (
          <div key={opt.id} className={`tv-btn ${i === idx ? 'tv-focused' : ''}`} onClick={() => select(opt)}
            style={{ display: 'flex', alignItems: 'center', gap: '1vw', padding: '1vw 1.5vw', cursor: 'pointer',
              background: i === idx ? '#E9641A' : 'rgba(255,255,255,0.07)' }}
            data-testid={`tv-settings-${opt.id}`}>
            <opt.Icon style={{ width: '1.4vw', height: '1.4vw', flex: 'none' }} />
            <div>
              <p style={{ fontSize: '1.2vw', fontWeight: 700 }}>{opt.label}</p>
              {opt.desc && <p style={{ fontSize: '0.9vw', color: i === idx ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.45)' }}>{opt.desc}</p>}
            </div>
          </div>
        ))}
      </div>

      <p style={{ marginTop: '2.5vw', fontSize: '0.9vw', color: 'rgba(255,255,255,0.3)' }}>Maya+ TV · Touche Retour pour revenir à l'accueil</p>

      {showDevices && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 25, background: 'rgba(5,2,10,0.94)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }} data-testid="tv-devices-overlay">
          <Monitor style={{ width: '2.5vw', height: '2.5vw', color: '#E9641A', marginBottom: '1vw' }} />
          <h2 style={{ fontSize: '1.8vw', fontWeight: 800, marginBottom: '0.6vw' }}>Appareils connectés</h2>
          <p style={{ fontSize: '0.95vw', color: 'rgba(255,255,255,0.5)', marginBottom: '1.8vw' }}>OK sur un appareil = le déconnecter (sa lecture s'arrête) · Retour pour fermer</p>
          <div style={{ width: '36vw', display: 'flex', flexDirection: 'column', gap: '0.7vw', maxHeight: '55vh', overflow: 'hidden' }}>
            {devices === null ? (
              <p style={{ fontSize: '1vw', color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>Chargement…</p>
            ) : devices.length === 0 ? (
              <p style={{ fontSize: '1vw', color: 'rgba(255,255,255,0.5)', textAlign: 'center' }} data-testid="tv-devices-empty">Aucun appareil connecté.</p>
            ) : devices.map((d, i) => {
              const Icon = d.kind === 'tv' ? Tv : d.kind === 'mobile' ? Smartphone : Monitor;
              return (
                <div key={d.id} className={`tv-btn ${i === devIdx ? 'tv-focused' : ''}`} onClick={() => disconnectDevice(d)}
                  style={{ display: 'flex', alignItems: 'center', gap: '1vw', padding: '0.9vw 1.4vw', cursor: 'pointer',
                    background: i === devIdx ? '#E9641A' : 'rgba(255,255,255,0.07)' }}
                  data-testid={`tv-device-${d.id}`}>
                  <Icon style={{ width: '1.4vw', height: '1.4vw', flex: 'none' }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: '1.1vw', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {d.name || KIND_LABEL[d.kind] || 'Appareil'}
                      {d.is_current && <span style={{ marginLeft: '0.6vw', fontSize: '0.75vw', fontWeight: 800, color: i === devIdx ? '#fff' : '#E9641A', textTransform: 'uppercase' }}>Cet appareil</span>}
                    </p>
                    <p style={{ fontSize: '0.85vw', color: i === devIdx ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.45)' }}>{KIND_LABEL[d.kind] || 'Appareil'}</p>
                  </div>
                  <span style={{ fontSize: '0.85vw', fontWeight: 700, color: i === devIdx ? '#fff' : 'rgba(255,255,255,0.4)' }}>Déconnecter</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showSubs && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 25, background: 'rgba(5,2,10,0.94)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }} data-testid="tv-subs-overlay">
          <Subtitles style={{ width: '2.5vw', height: '2.5vw', color: '#E9641A', marginBottom: '1vw' }} />
          <h2 style={{ fontSize: '1.8vw', fontWeight: 800, marginBottom: '0.6vw' }}>Style des sous-titres</h2>
          <p style={{ fontSize: '0.95vw', color: 'rgba(255,255,255,0.5)', marginBottom: '1.8vw' }}>←/→ pour changer · Haut/Bas pour passer d'un réglage à l'autre · OK ou Retour pour fermer</p>
          {[{ label: 'Taille', list: SUB_SIZES, key: 'size' }, { label: 'Fond', list: SUB_BGS, key: 'bg' }].map((row, ri) => (
            <div key={row.key} style={{ display: 'flex', alignItems: 'center', marginBottom: '1.2vw' }} data-testid={`tv-subs-row-${row.key}`}>
              <span style={{ fontSize: '1.15vw', fontWeight: 700, width: '7vw', marginRight: '1.5vw', textAlign: 'right', color: subRow === ri ? '#fff' : 'rgba(255,255,255,0.45)' }}>{row.label}</span>
              <div style={{ display: 'flex' }}>
                {row.list.map(o => {
                  const sel = subStyle[row.key] === o.id;
                  return (
                    <div key={o.id} data-testid={`tv-subs-${row.key}-${o.id}`}
                      style={{ padding: '0.55vw 1.5vw', marginRight: '0.8vw', borderRadius: '2vw', fontSize: '1.05vw', fontWeight: 700, cursor: 'pointer',
                        background: sel ? (subRow === ri ? '#E9641A' : 'rgba(233,100,26,0.45)') : 'rgba(255,255,255,0.08)',
                        border: sel && subRow === ri ? '0.15vw solid #fff' : '0.15vw solid transparent',
                        color: sel ? '#fff' : 'rgba(255,255,255,0.6)' }}
                      onClick={() => saveSubStyle({ ...subStyle, [row.key]: o.id })}>
                      {o.label}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <div style={{ marginTop: '2vw', minHeight: '6vw', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span data-testid="tv-subs-preview" style={{ fontSize: SUB_FONT[subStyle.size], fontWeight: 600, color: '#fff', textAlign: 'center', lineHeight: 1.35, ...SUB_BG_CSS[subStyle.bg] }}>
              Aperçu des sous-titres
            </span>
          </div>
          <p style={{ marginTop: '1.5vw', fontSize: '0.85vw', color: 'rgba(255,255,255,0.35)' }}>Le réglage est enregistré automatiquement et s'applique à toutes les lectures sur cette TV.</p>
        </div>
      )}

      {showHelp && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 25, background: 'rgba(5,2,10,0.94)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }} data-testid="tv-help">
          <HelpCircle style={{ width: '2.5vw', height: '2.5vw', color: '#E9641A', marginBottom: '1vw' }} />
          <h2 style={{ fontSize: '1.8vw', fontWeight: 800, marginBottom: '1.5vw' }}>Aide Maya+ TV</h2>
          <div style={{ maxWidth: '38vw', display: 'flex', flexDirection: 'column', gap: '0.9vw', fontSize: '1.05vw', color: 'rgba(255,255,255,0.75)', lineHeight: 1.5 }}>
            <p>• Utilisez les <b>flèches</b> de la télécommande pour naviguer, <b>OK</b> pour valider et <b>Retour</b> pour revenir en arrière.</p>
            <p>• Pour connecter un nouvel appareil, rendez-vous sur la page <b>/activate</b> du site Maya+ depuis votre téléphone et saisissez le code affiché sur la TV.</p>
            <p>• {isFireTvWrapper ? <>Gérez vos <b>profils</b> et appareils connectés depuis <b>Mon compte</b>.</> : <>Gérez vos profils, abonnements et appareils connectés depuis <b>Mon compte</b> sur le site web Maya+.</>}</p>
            <p>• Un profil <b>Enfant</b> n'affiche que les contenus jeunesse et les jeux.</p>
          </div>
          <p style={{ marginTop: '2vw', fontSize: '0.9vw', color: 'rgba(255,255,255,0.35)' }}>Appuyez sur OK ou Retour pour fermer</p>
        </div>
      )}
    </div>
  );
}
